// filepath: apps/web/src/features/proc/index-shared.ts
//
// FR-UI-PROC-1 — colocated helpers + re-exports consumed by the
// barrel.
//
// Two classify* helpers (one for the dashboard, one for the
// trace viewer) live here so the public API can export them
// without circular imports between the components and the barrel.
//
// The cache-key factory (`procKeys`) and the `RESTORED_ORPHAN_REASON`
// constant are re-exported here so the barrel needs only a single
// import path. The actual definitions live in `./types` (where
// they are colocated with the wire types they describe).
//
// Mirror of the `classifyListError` export at
// apps/web/src/features/lists/index.ts:9-12.

import { ApiError } from "@/definitions/types";
import type { FailedInstanceStatus } from "./types";

export { procKeys, RESTORED_ORPHAN_REASON } from "./types";

/**
 * Classify a failed-instance dashboard ApiError into one of the
 * 5 mandatory renderer states (FR-UI-CORE-10).
 *
 * The dashboard body imports the function from this file directly
 * to avoid a barrel-import cycle.
 */
export function classifyFailedInstanceError(
  err: ApiError,
): Exclude<FailedInstanceStatus, "loading" | "success"> {
  if (err.status === 401 || err.status === 403) return "permission-denied";
  if (err.status === 429) return "rate-limit";
  if (err.status === 410 || err.status === 404) return "stale-version";
  return "fetch-failure";
}

/** 5-state classification for the step-log fetch surface. */
export type StepLogStatus =
  "fetch-failure" | "permission-denied" | "stale-version" | "rate-limit";

/**
 * Classify a step-log fetch ApiError into one of the 4 error states.
 * The loading + success states are the path-through branches in the
 * viewer render; only the four below come from `ApiError`.
 */
export function classifyStepLogError(err: ApiError): StepLogStatus {
  if (err.status === 401 || err.status === 403) return "permission-denied";
  if (err.status === 429) return "rate-limit";
  if (err.status === 410 || err.status === 404) return "stale-version";
  return "fetch-failure";
}
